/**
 * ご利用プラン。
 *
 * 以前は `plans` コレクション（0件）を読み、月間予約件数・スタッフ数・
 * メニュー数で課金する別のプラン体系を出していた。
 * `shop.planId` を見ていたが、実データが持つのは `planTier` なので
 * 何を入れても現在のプランは判定できなかった。
 *
 * この製品の料金は utils/planGate.ts が唯一の出どころで、
 * ひと月に切った伝票の数だけで決まる（人数でも端末数でもない）。
 * LP・特商法・マニュアル・アプリ（plan_tiers.dart）と同じ値を出す。
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  List, ListItem, ListItemIcon, ListItemText, Paper, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  PLAN_LABELS, TIER_DESCRIPTIONS, TIER_MONTHLY_PRICE,
  monthlyPriceIncludingTax,
  evaluatePlan, billingSlips, type PlanTier, type ShopUsage,
} from '../../utils/planGate';
import { getBusinessDayRange, resolveBoundaryHour } from '../../utils/businessDate';

const TIERS: PlanTier[] = ['free', 'standard', 'pro'];

const PlanSettings: React.FC = () => {
  const { shop } = useAuth();
  const [usage, setUsage] = useState<ShopUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shop?.id) return;
    (async () => {
      try {
        setLoading(true);
        // 当月の伝票数。
        //
        //   もとは checkoutAt という日時で絞っていたが、
        //   伝票にその項目は無い（会計が書くのは businessDate と createdAt）。
        //   Firestore は無い項目で絞ると 0 件を返すだけで、
        //   エラーにならない。そのため、どの店も伝票 0 件と判定され、
        //   全店が無料の段に見えていた。課金の土台が動いていなかった。
        //
        //   businessDate は 'YYYY-MM-DD' の文字列で、
        //   会計のときに営業日の区切りを当てたうえで作られている。
        //   ここで区切りを当て直す必要はない（二重に当てると1日ずれる）。
        const now = new Date();
        const months: number[] = [];
        for (let back = 0; back < 3; back++) {
          const d = new Date(now.getFullYear(), now.getMonth() - back, 1);
          const nd = new Date(d.getFullYear(), d.getMonth() + 1, 1);
          const ym = (x: Date) =>
            `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-01`;
          const snap = await getDocs(query(
            collection(db, 'checkouts'),
            where('shopId', '==', shop.id),
            where('businessDate', '>=', ym(d)),
            where('businessDate', '<', ym(nd)),
          ));
          months.push(snap.size);
        }
        setUsage({
          slipsThisMonth: months[0],
          peakMonthlySlips: Math.max(...months),
          employeeCount: 0,
          shopCount: 1,
        });
      } catch (e: any) {
        setError(e?.message ?? '伝票数を数えられませんでした');
      } finally {
        setLoading(false);
      }
    })();
  }, [shop]);

  const currentTier: PlanTier =
    (['free', 'standard', 'pro'] as const).find((t) => t === (shop as any)?.planTier) ?? 'free';
  const gate = usage ? evaluatePlan(currentTier, usage) : null;

  // 申し込みと解約。カード番号はこちらの画面を通らない。
  // Stripe の画面に飛ばし、結果は webhook で受け取る。
  // 画面側で段を書き換えないのは、押しただけで段が上がってしまうため。
  // 段を書けるのは webhook だけで、ルールでも塞いである。
  const [working, setWorking] = useState<PlanTier | 'portal' | null>(null);

  const goStripe = async (fn: string, data: Record<string, unknown>, mark: PlanTier | 'portal') => {
    if (working !== null) return;   // 二重に申し込ませない
    setError(null);
    setWorking(mark);
    try {
      const call = httpsCallable<Record<string, unknown>, { url?: string }>(functions, fn);
      // 返ってこないときに押せないまま固まらないよう、待つ時間を区切る。
      const res = await Promise.race([
        call(data),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Stripe の画面を用意できませんでした（時間切れ）')), 20000)),
      ]);
      const url = (res as { data?: { url?: string } })?.data?.url;
      if (!url) throw new Error('Stripe の画面のURLが返りませんでした');
      window.location.href = url;
    } catch (e: any) {
      // 「価格が登録されていない」なども、そのまま出す。
      // 黙って何も起きないと、押した人は壊れていると思う。
      setError(e?.message ?? '手続きを開始できませんでした');
      setWorking(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>ご利用プラン</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        料金はひと月に切った伝票の数だけで決まります。
        従業員の人数でも、端末の台数でも、使う機能でも変わりません。
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">いまの段</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Typography variant="h6">{PLAN_LABELS[currentTier]}</Typography>
          <Chip
            size="small"
            label={TIER_MONTHLY_PRICE[currentTier] === 0
              ? '無料'
              : `月額 ${TIER_MONTHLY_PRICE[currentTier].toLocaleString()} 円（税抜） / ` +
                `${monthlyPriceIncludingTax(currentTier).toLocaleString()} 円（税込）`}
          />
        </Box>
        {loading ? (
          <Box sx={{ mt: 2 }}><CircularProgress size={20} /></Box>
        ) : gate && usage ? (
          <>
            <Typography variant="body2" sx={{ mt: 1.5 }}>{gate.message}</Typography>
            <Typography variant="caption" color="text.secondary">
              当月 {usage.slipsThisMonth.toLocaleString()} 件 /
              直近3ヶ月の最大 {(usage.peakMonthlySlips ?? usage.slipsThisMonth).toLocaleString()} 件 /
              判定に使う数 {billingSlips(usage).toLocaleString()} 件
            </Typography>
          </>
        ) : null}
      </Paper>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
        {TIERS.map((t) => (
          <Card key={t} variant={t === currentTier ? 'elevation' : 'outlined'}
                elevation={t === currentTier ? 6 : 0}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">{PLAN_LABELS[t]}</Typography>
                {t === currentTier && <Chip size="small" color="primary" label="いまの段" />}
              </Box>
              <Typography variant="h5" sx={{ mt: 1 }}>{TIER_DESCRIPTIONS[t].price}</Typography>
              {TIER_MONTHLY_PRICE[t] > 0 && (
                <Typography variant="caption" color="text.secondary">
                  お支払いは税込 {monthlyPriceIncludingTax(t).toLocaleString()} 円
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {TIER_DESCRIPTIONS[t].slips}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="body2">{TIER_DESCRIPTIONS[t].note}</Typography>
              <List dense>
                {['機能はどの段でも同じ', '従業員の人数は無制限',
                  t === 'pro' ? '店舗をまたいだ管理' : '1店舗'].map((f) => (
                  <ListItem key={f} disableGutters>
                    <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={f} />
                  </ListItem>
                ))}
              </List>
              {/* 押した側だけが動いていると分かるようにする。
                  はじめ disabled={working !== null} を全部のボタンに付けていたため、
                  スタンダードを押すとプロのボタンまで灰色になり、
                  「両方押された」ように見えていた。
                  押した側は輪を回し、押していない側はそのまま置いておく。
                  二重に申し込ませない用心は goStripe の中で行う。 */}
              {t !== 'free' && t !== currentTier && (
                <Button
                  fullWidth
                  variant="contained"
                  disabled={working === t}
                  startIcon={working === t ? <CircularProgress size={16} color="inherit" /> : undefined}
                  onClick={() => goStripe('createPlanCheckout', { shopId: shop?.id, tier: t }, t)}
                >
                  {working === t ? '準備しています…' : `${PLAN_LABELS[t]}に申し込む`}
                </Button>
              )}
              {t === currentTier && t !== 'free' && (
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={working === 'portal'}
                  startIcon={working === 'portal' ? <CircularProgress size={16} color="inherit" /> : undefined}
                  onClick={() => goStripe('createPlanPortal', { shopId: shop?.id }, 'portal')}
                >
                  {working === 'portal' ? '準備しています…' : '支払い方法の変更・解約'}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        上限を超えても会計は止まりません。お客様を待たせないためです。
        段は、当月の伝票数と直近3ヶ月の最大の、大きいほうで判定します。
      </Alert>
    </Box>
  );
};

export default PlanSettings;
